import React, { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import pallet from '../../../layouts/pallet';
import { StyledButton } from '../../Exams/styled';
import { SkillResponse, SkillsTableAndActionsProps } from '../types';
import { getSkillTableColumns } from '../utils/skillTableColumns';
import { dataGridSx } from '../../common/styled/DataGrid';
import Icons from '../../../components/icons';
import { PageWrapper } from '../../common/styled';
import {
  FullWidthBox,
  ResultCountText,
  SkillsActionsContainer,
  SkillsButtonsRow
} from '../styled';
import AddSkillModal from './AddSkillModal';
import CloneSkillsModal from './CloneSkillsModal';
import ViewEditSkillModal from './ViewEditSkillModal';

const SkillsTableAndActions: React.FC<SkillsTableAndActionsProps> = ({
  skills,
  academicYear,
  grade,
  division,
  allClasses,
  school,
  onSkillAdded,
  onSkillsCloned,
  onSkillUpdated,
  showToast
}) => {
  const [isAddSkillModalOpen, setIsAddSkillModalOpen] = useState(false);
  const [isCloneSkillsModalOpen, setIsCloneSkillsModalOpen] = useState(false);
  const [isViewEditSkillModalOpen, setIsViewEditSkillModalOpen] =
    useState(false);
  const [selectedSkill, setSelectedSkill] = useState<SkillResponse | undefined>(
    undefined
  );

  const handleEditClick = (skill: SkillResponse) => {
    setSelectedSkill(skill);
    setIsViewEditSkillModalOpen(true);
  };

  const handleOpenAddSkill = () => setIsAddSkillModalOpen(true);
  const handleCloseAddSkill = () => setIsAddSkillModalOpen(false);
  const handleOpenCloneSkills = () => setIsCloneSkillsModalOpen(true);
  const handleCloseCloneSkills = () => setIsCloneSkillsModalOpen(false);
  const handleCloseViewEditSkill = () => setIsViewEditSkillModalOpen(false);

  const handleSkillAdded = () => {
    handleCloseAddSkill();
    onSkillAdded();
  };

  const handleSkillsCloned = () => {
    handleCloseCloneSkills();
    onSkillsCloned();
  };

  const handleSkillUpdated = () => {
    handleCloseViewEditSkill();
    onSkillUpdated();
  };

  const columns = getSkillTableColumns(handleEditClick);

  return (
    <>
      <PageWrapper>
        <SkillsActionsContainer>
          <SkillsButtonsRow>
            {skills.length === 0 && (
              <StyledButton onClick={handleOpenCloneSkills}>
                Clone Skills
              </StyledButton>
            )}
            <StyledButton onClick={handleOpenAddSkill}>
              <Icons.PlusWhite style={{ marginRight: '6px' }} /> Add Skill
            </StyledButton>
          </SkillsButtonsRow>
        </SkillsActionsContainer>
        <FullWidthBox>
          <DataGrid
            rows={skills.map((skill) => ({
              ...skill,
              totalSubSkills: skill.subSkills?.length || 0
            }))}
            columns={columns}
            disableRowSelectionOnClick
            pageSizeOptions={[5, 10, 25]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 }
              }
            }}
            sx={{
              ...dataGridSx(pallet),
              '& .MuiDataGrid-virtualScroller': {
                overflowY: 'auto'
              }
            }}
          />
        </FullWidthBox>
        <ResultCountText variant='body2'>
          {skills.length} results
        </ResultCountText>
      </PageWrapper>

      <AddSkillModal
        open={isAddSkillModalOpen}
        onClose={handleCloseAddSkill}
        academicYear={academicYear}
        grade={grade}
        division={division}
        school={school}
        onSkillAdded={handleSkillAdded}
        showToast={showToast}
      />

      <CloneSkillsModal
        open={isCloneSkillsModalOpen}
        onClose={handleCloseCloneSkills}
        academicYear={academicYear}
        grade={grade}
        division={division}
        allClasses={allClasses}
        school={school}
        onSkillsCloned={handleSkillsCloned}
        showToast={showToast}
      />

      <ViewEditSkillModal
        open={isViewEditSkillModalOpen}
        onClose={handleCloseViewEditSkill}
        skill={selectedSkill}
        academicYear={academicYear}
        grade={grade}
        division={division}
        school={school}
        onSkillUpdated={handleSkillUpdated}
        showToast={showToast}
      />
    </>
  );
};

export default SkillsTableAndActions;
